import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import UserFormModal from "../components/UserFormModal";
import ConfirmModal from "../components/ConfirmModal";
import {
  userService,
  User,
  UserPaginationInfo,
  CreateUserData,
  UpdateUserData,
} from "../services/userService";
import "./ManageUsersPage.css";

const ManageUsersPage: React.FC = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<UserPaginationInfo>({
    page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    id: number;
    username: string;
  } | null>(null);

  // Authorization check
  useEffect(() => {
    if (!user?.is_superuser) {
      navigate("/chats");
      return;
    }
  }, [user, navigate]);

  // Fetch users
  useEffect(() => {
    if (user?.is_superuser) {
      fetchUsers();
    }
  }, [pagination.page, pagination.page_size]);

  const fetchUsers = async () => {
    if (!token) return;

    setLoading(true);
    setError("");

    try {
      const response = await userService.getUsers(
        token,
        pagination.page,
        pagination.page_size,
        searchQuery
      );

      setUsers(response.users);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleCreateUser = () => {
    setModalMode("create");
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setModalMode("edit");
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (userId: number, username: string) => {
    setUserToDelete({ id: userId, username: username });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !userToDelete) return;

    try {
      await userService.deleteUser(token, userToDelete.id);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete user");
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleFormSubmit = async (userData: CreateUserData | UpdateUserData) => {
    if (!token) return;

    try {
      if (modalMode === "create") {
        await userService.createUser(token, userData as CreateUserData);
      } else if (selectedUser) {
        await userService.updateUser(token, selectedUser.id, userData as UpdateUserData);
      }
      fetchUsers();
      setIsModalOpen(false);
    } catch (err) {
      throw err;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="manage-users-container">
      <PageHeader
        title="Manage Users"
        showBackButton={true}
        backTo="/chats"
        username={user?.username || "User"}
        isSuperuser={user?.is_superuser}
        onLogout={handleLogout}
        onManageKnowledge={() => navigate("/knowledge-documents")}
        onManageUsers={() => navigate("/manage-users")}
      />

      <main className="main">
        <div className="controls-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search users by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              Search
            </button>
          </form>

          <button className="create-button" onClick={handleCreateUser}>
            + Create User
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading-message">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="empty-message">No users found</div>
        ) : (
          <>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Staff</th>
                    <th>Superuser</th>
                    <th>Date Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((usr) => (
                    <tr key={usr.id}>
                      <td>{usr.id}</td>
                      <td>{usr.username}</td>
                      <td>{usr.email}</td>
                      <td>
                        <span
                          className={`status-badge ${
                            usr.is_active ? "active" : "inactive"
                          }`}
                        >
                          {usr.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className={usr.is_staff ? "text-success" : "text-muted"}>
                          {usr.is_staff ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <span className={usr.is_superuser ? "text-success" : "text-muted"}>
                          {usr.is_superuser ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        {usr.date_joined
                          ? new Date(usr.date_joined).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-button edit-button"
                            onClick={() => handleEditUser(usr)}
                          >
                            Edit
                          </button>
                          <button
                            className="action-button delete-button"
                            onClick={() => handleDeleteClick(usr.id, usr.username)}
                            disabled={usr.id === user?.id}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              hasNext={pagination.has_next}
              hasPrevious={pagination.has_previous}
              onPageChange={handlePageChange}
              pageSize={pagination.page_size}
              onPageSizeChange={handlePageSizeChange}
              totalItems={pagination.total_items}
            />
          </>
        )}
      </main>

      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleFormSubmit}
        user={selectedUser}
        mode={modalMode}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.username}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />
    </div>
  );
};

export default ManageUsersPage;
